// FAQ data for common questions about Mateo
// These responses are used before calling GPT to reduce token usage

export interface FAQItem {
  keywords: string[]
  relatedTerms?: string[] // NEW: Synonyms and related terms for fuzzy matching
  response: string
  caseStudyCards?: Array<{
    slug: string
    title: string
    relevance: number
    description: string
  }>
}

export const faqData: FAQItem[] = [
  // AI & Machine Learning
  {
    keywords: ['ai', 'artificial intelligence', 'machine learning', 'ml'],
    relatedTerms: [
      'neural network',
      'deep learning',
      'nlp',
      'natural language',
      'automation',
      'intelligent systems',
    ],
    response:
      'has strong AI experience across multiple projects. He compared major AI platforms (Watson vs Dialogflow) and built an AI-powered CV parsing system with 85%+ accuracy at Case Western.',
    caseStudyCards: [
      {
        slug: 'ai-comparison',
        title: 'Watson vs Dialogflow',
        relevance: 0.95,
        description:
          'Conversational AI platform comparison and chatbot implementation',
      },
      {
        slug: 'case-western',
        title: 'Case Western Reserve University',
        relevance: 0.9,
        description: 'AI-powered CV parsing with 85%+ accuracy',
      },
    ],
  },

  // Financial Services
  {
    keywords: ['financial', 'finance', 'banking', 'fintech', 'us bank', 'bank'],
    relatedTerms: [
      'payment',
      'corporate card',
      'transaction',
      'money',
      'credit',
    ],
    response:
      'worked on the US Bank Corporate Payment Services team, redesigning the One Card application. He improved customer satisfaction from 50% to 98% and reduced processing time from 54 days to 20 days.',
    caseStudyCards: [
      {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        relevance: 1.0,
        description: 'Corporate payment services and enterprise UX design',
      },
    ],
  },

  // Cybersecurity
  {
    keywords: ['security', 'cybersecurity', 'cyber', 'unisys', 'stealth'],
    relatedTerms: [
      'threat',
      'network security',
      'data protection',
      'vulnerability',
      'encryption',
    ],
    response:
      'redesigned Unisys Stealth, a complex cybersecurity platform. He transformed technical micro-segmentation and threat analysis systems into intuitive interfaces for security professionals.',
    caseStudyCards: [
      {
        slug: 'cyber-security',
        title: 'Cyber Security Platform Redesign',
        relevance: 1.0,
        description:
          'Complex data visualization and security platform redesign',
      },
    ],
  },

  // UX Research
  {
    keywords: [
      'research',
      'user research',
      'interviews',
      'testing',
      'usability',
    ],
    relatedTerms: [
      'user testing',
      'qualitative',
      'quantitative',
      'survey',
      'feedback',
      'user study',
    ],
    response:
      "has extensive UX research experience. At Case Western, he conducted 16 user interviews in a two-week sprint and used affinity diagramming to identify critical pain points. He's skilled in qualitative research and user testing.",
    caseStudyCards: [
      {
        slug: 'case-western',
        title: 'Case Western Reserve University',
        relevance: 1.0,
        description: '16 user interviews and qualitative research sprint',
      },
      {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        relevance: 0.85,
        description: 'User-centered design and iterative testing',
      },
    ],
  },

  // Design Systems
  {
    keywords: [
      'design system',
      'component library',
      'design tokens',
      'components',
    ],
    relatedTerms: [
      'ui library',
      'pattern library',
      'style guide',
      'design language',
    ],
    response:
      'has strong design system experience. At US Bank, he updated 75% of outdated components and created 20 new local components, including a vertical navigation bar adopted by other teams.',
    caseStudyCards: [
      {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        relevance: 1.0,
        description: 'Design system alignment and component creation',
      },
    ],
  },

  // Conversational Design / Chatbots
  {
    keywords: [
      'chatbot',
      'conversational',
      'dialogue',
      'watson',
      'dialogflow',
      'nlp',
      'nlu',
    ],
    relatedTerms: [
      'voice interface',
      'chat interface',
      'virtual assistant',
      'bot',
      'conversation flow',
    ],
    response:
      'has deep conversational AI experience. He compared IBM Watson and Google Dialogflow, built working chatbot demos for MoneyGram, and designed natural dialogue patterns that reduced call center volume.',
    caseStudyCards: [
      {
        slug: 'ai-comparison',
        title: 'Watson vs Dialogflow',
        relevance: 1.0,
        description: 'Conversational AI platform comparison and implementation',
      },
    ],
  },

  // Data Visualization
  {
    keywords: [
      'data visualization',
      'data viz',
      'charts',
      'graphs',
      'dashboards',
    ],
    relatedTerms: [
      'analytics',
      'metrics',
      'reporting',
      'visual analytics',
      'infographics',
    ],
    response:
      'excels at data visualization. He redesigned complex security threat visualizations at Unisys and created intuitive dashboards for technical users, balancing technical depth with usability.',
    caseStudyCards: [
      {
        slug: 'cyber-security',
        title: 'Cyber Security Platform Redesign',
        relevance: 1.0,
        description: 'Complex data visualization for security threats',
      },
    ],
  },

  // Enterprise / B2B
  {
    keywords: ['enterprise', 'b2b', 'saas', 'corporate', 'business'],
    relatedTerms: [
      'business software',
      'workplace',
      'professional',
      'commercial',
    ],
    response:
      "specializes in enterprise B2B applications. He's worked on corporate payment systems at US Bank, cybersecurity platforms at Unisys, and faculty management systems at Case Western.",
    caseStudyCards: [
      {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        relevance: 0.95,
        description: 'Enterprise B2B payment application',
      },
      {
        slug: 'cyber-security',
        title: 'Cyber Security Platform Redesign',
        relevance: 0.9,
        description: 'Enterprise security platform',
      },
    ],
  },

  // Accessibility
  {
    keywords: ['accessibility', 'a11y', 'wcag', 'inclusive', 'ada'],
    relatedTerms: [
      'screen reader',
      'keyboard navigation',
      'color contrast',
      'inclusive design',
    ],
    response:
      "prioritizes accessibility in all his work. At US Bank, he partnered with accessibility specialist Tracey Jackson to ensure every screen met WCAG 2.1 standards, making accessibility part of the team's definition of done.",
    caseStudyCards: [
      {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        relevance: 1.0,
        description: 'WCAG 2.1 compliance and accessibility-first design',
      },
    ],
  },

  // Skills Overview
  {
    keywords: [
      'skills',
      'expertise',
      'what can you do',
      'capabilities',
      'experience',
    ],
    relatedTerms: ['abilities', 'proficiency', 'competency', 'background'],
    response:
      "is a strategic product designer with expertise in UX research, AI innovation, enterprise applications, and design systems. He's worked across financial services, cybersecurity, and education sectors.",
    caseStudyCards: [
      {
        slug: 'case-western',
        title: 'Case Western Reserve University',
        relevance: 0.95,
        description: 'UX research and AI innovation',
      },
      {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        relevance: 0.9,
        description: 'Enterprise UX and design systems',
      },
    ],
  },

  // Years of Experience
  {
    keywords: [
      'years of experience',
      'how long',
      'how many years',
      'years in design',
      'experience level',
      'how experienced',
    ],
    relatedTerms: [
      'seniority',
      'tenure',
      'career length',
      'time in field',
      'professional experience',
      'work history',
    ],
    response:
      "has 8+ years of professional design experience. He started in startups and fell into design, then pursued formal design education. Over nearly a decade, he's worked across financial services (US Bank), cybersecurity (Unisys), education (Case Western), and fintech (MoneyGram).",
    caseStudyCards: [],
  },

  // Contact / Availability
  {
    keywords: [
      'contact',
      'hire',
      'available',
      'reach out',
      'email',
      'linkedin',
    ],
    relatedTerms: [
      'get in touch',
      'connect',
      'availability',
      'open to opportunities',
    ],
    response:
      "is currently open to new opportunities! You can connect with him on LinkedIn or reach out through this portfolio. He's particularly interested in roles involving AI, enterprise applications, and design leadership.",
  },
]

/**
 * Check if user query matches any FAQ with fuzzy matching
 * Returns FAQ response with confidence score if match found, null otherwise
 */
export function checkFAQ(userQuery: string): {
  message: string
  caseStudyCards?: Array<{
    slug: string
    title: string
    relevance: number
    description: string
  }>
  confidence: number
} | null {
  const normalizedQuery = userQuery.toLowerCase()

  let bestMatch: FAQItem | null = null
  let highestScore = 0

  // Find best matching FAQ with confidence scoring
  for (const faq of faqData) {
    let score = 0

    // Check exact keyword matches (weight: 1.0)
    for (const keyword of faq.keywords) {
      if (normalizedQuery.includes(keyword.toLowerCase())) {
        score += 1.0
      }
    }

    // Check related terms (weight: 0.5)
    if (faq.relatedTerms) {
      for (const term of faq.relatedTerms) {
        if (normalizedQuery.includes(term.toLowerCase())) {
          score += 0.5
        }
      }
    }

    if (score > highestScore) {
      highestScore = score
      bestMatch = faq
    }
  }

  // Return match if we have sufficient confidence
  if (bestMatch && highestScore > 0) {
    return {
      message: bestMatch.response,
      caseStudyCards: bestMatch.caseStudyCards,
      confidence: highestScore,
    }
  }

  return null
}
